import React, { useMemo } from 'react'
import moment from 'moment'
import BedbankPackageTilePricing from './BedbankPackageTilePricing'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BedbankCancellationPolicy from './BedbankCancellationPolicy'
import AccommodationOfferInclusionsExpandable from 'components/OfferPage/Accommodation/AccommodationOfferInclusionsExpandable'
import WalledContent from 'components/Common/WalledContent'
import AccommodationPackageTile from 'components/AccommodationKit/AccommodationPackageTile'
import AccommodationPackageTileContentArea from 'components/AccommodationKit/AccommodationPackageTileContentArea'
import AccommodationPackageTileDetails from 'components/AccommodationKit/AccommodationPackageTileDetails'
import BedbankPromotionDetails from './BedbankPromotionDetails'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { generateOfferPageRoom, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import Group from 'components/utils/Group'
import AccommodationPackageTilePricingArea from 'components/AccommodationKit/AccommodationPackageTilePricingArea'
import { useBedbankSingleRoomBookNowCallback, useBedbankSingleRoomAddToUserCartCallback } from 'hooks/OfferPage/useBedbankBuyCallbacks'
import useRoomOptionRateBedbankQuote from 'hooks/Insurance/useRoomOptionRateBedbankQuote'
import BedbankRoomOptionRateInsurance from './BedbankRoomOptionRateInsurance'
import styled from 'styled-components'
import { rem } from 'polished'
import { FLIGHTS_MIN_LEAD_TIME_DAYS } from 'constants/flight'

const BedbankRoomOptionRateInsuranceContainer = styled.div`
  margin-top: ${rem(8)};
`

const defaultRooms = [generateOfferPageRoom()]

interface Props {
  packageView: App.BedbankOfferPackageView
  rate: App.BedbankRate
  checkInDate?: string
  checkOutDate?: string
  offer: App.BedbankOffer
  rooms?: Array<OfferPageRoom>
  optionNumber: number
  isInitialPackageOption?: boolean

  subscriber?: App.OrderSubscriber
  isSpoofed?: boolean
  isLoggedIn?: boolean
  originAirport?: App.Airport
  flights?: boolean

  /**
   * Unique ID for the list of rates this rate was shown under
   * Required for analytics
   */
  rateListId: string
  /**
   * Position this room was in the list of rooms on the offer page
   * Required for analytics
   */
  roomPosition: number
  /**
   * Unique ID for the list of rooms on the offer page
   * Required for analytics
   */
  roomListId: string
  showOptionLabel?: boolean
  connectRooms: boolean
  isFlexCfmrBedbankPackagesEnabled?: boolean
}

function BedbankRoomOptionRate(props: Props) {
  const {
    offer,
    rooms = defaultRooms,
    checkInDate,
    checkOutDate,
    roomListId,
    roomPosition,
    isInitialPackageOption,
    rateListId,
    optionNumber,
    packageView,
    rate,
    subscriber,
    isLoggedIn,
    isSpoofed,
    originAirport,
    showOptionLabel = true,
    connectRooms,
    isFlexCfmrBedbankPackagesEnabled
  } = props

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: originAirport?.code,
  })

  const { bundleRates, promotions } = packageView
  const bundleRate = useMemo(() => bundleRates.find((bundleRate) => bundleRate.id === rate.id) ?? rate, [bundleRates, rate])

  const departureTooEarly = useMemo(() => {
    return !!checkInDate && moment(checkInDate).diff(moment(), 'days') < FLIGHTS_MIN_LEAD_TIME_DAYS
  }, [checkInDate])

  const onBook = useBedbankSingleRoomBookNowCallback({
    offer,
    packageView,
    checkInDate,
    checkOutDate,
    rooms,
    originAirport,
    isLoggedIn,
    isSpoofed,
    subscriber,
    rateListId,
    roomPosition,
    roomListId,
    flightPrice,
    connectRooms
  })

  const onAddToUserCart = useBedbankSingleRoomAddToUserCartCallback({
    offer,
    packageView,
    checkInDate,
    checkOutDate,
    rooms,
    originAirport,
    isLoggedIn,
    isSpoofed,
    subscriber,
    rateListId,
    roomPosition,
    roomListId,
    flightPrice,
    connectRooms,
  })

  const { quote, isFetching: isQuoteFetching, error: quoteError, isInsuranceSelected, onSelectInsurance } = useRoomOptionRateBedbankQuote({
    country: offer.property.address.countryName,
    coverAmount: rate.totals.total,
    checkIn: checkInDate,
    checkout: checkOutDate,
    travellers: rooms,
    useCache: true,
    disabled: !isFlexCfmrBedbankPackagesEnabled
  })

  const promotion = promotions[rate.promotionId ?? '']
  const isRefundableRate = rate.refundable || rate.partiallyRefundable

  return <AccommodationPackageTile>
    <AccommodationPackageTileContentArea>
      <AccommodationPackageTileDetails
        packageNumber={showOptionLabel ? optionNumber : undefined}
        promotion={promotion}
        tagLineLabel={<>
          {rate.corporate && <ProductTypeLabel productType="corporate" />}
          {promotion && <ProductTypeLabel productType="limited_time_special" />}
        </>}
      >
        {promotion && <BedbankPromotionDetails
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          promotion={promotion}
          rate={rate}
          showNonCancellation={!isInitialPackageOption}
        />}
        <Group direction="vertical" gap={8} noDisplayWhenEmpty>
          {!promotion && <BedbankCancellationPolicy
            rate={rate}
            showNonCancellation={!isFlexCfmrBedbankPackagesEnabled}
          />}
        </Group>
        {!isRefundableRate && isFlexCfmrBedbankPackagesEnabled && <BedbankRoomOptionRateInsuranceContainer>
          <BedbankRoomOptionRateInsurance
            isLoading={isQuoteFetching}
            onChange={onSelectInsurance}
            errorOnQuote={!!quoteError}
            quote={quote}
            refundTitle="Full refund for unforeseen events"
            refundDetails="(Hotel only)"
          />
        </BedbankRoomOptionRateInsuranceContainer>}
      </AccommodationPackageTileDetails>
      <div>
        {promotion && <AccommodationOfferInclusionsExpandable
          content={promotion.rateInclusionsLong}
          inclusions={promotion.inclusions}
          maxHeight={180}
        />}
        {!promotion && <VerticalSpacer gap={4}>
          {rate.inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
        </VerticalSpacer>}
      </div>
    </AccommodationPackageTileContentArea>
    <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing}>
      <AccommodationPackageTilePricingArea>
        <BedbankPackageTilePricing
          promotion={promotion}
          rate={rate}
          bundleRate={bundleRate}
          payAtPropertyRates={packageView.payAtPropertyRates}
          flightPrice={flightPrice}
          departureTooEarly={departureTooEarly}
          ratePosition={optionNumber}
          rateListId={rateListId}
          roomPosition={roomPosition}
          roomListId={roomListId}
          onBookNow={onBook}
          onAddToUserCartNow={onAddToUserCart}
          showPoints
          isInsuranceSelected={isInsuranceSelected}
        />
      </AccommodationPackageTilePricingArea>
    </WalledContent>
  </AccommodationPackageTile>
}

type MappedProps = Pick<Props,
  | 'checkInDate'
  | 'checkOutDate'
  | 'rooms'
  | 'subscriber'
  | 'isLoggedIn'
  | 'isSpoofed'
  | 'originAirport'
  | 'flights'
  | 'connectRooms'
>

export default withOfferPageAndReduxState((offerState, appState): MappedProps => ({
  checkInDate: offerState.checkInDate,
  checkOutDate: getCheckoutDate(offerState),
  rooms: offerState.rooms,
  subscriber: getEnquirySubscriber(appState),
  isLoggedIn: !!appState.auth.account.memberId,
  isSpoofed: appState.auth.account.isSpoofed,
  originAirport: offerState.airport,
  connectRooms: !!offerState.isMultiRoomConnected,
}), BedbankRoomOptionRate)
